import React, { CSSProperties, useMemo } from 'react';
import { Button, ButtonProps, IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { FontIcon } from '@moderepo/mode-app-components';
import { SingleMetricData } from '.';
import { useSelectProject } from '../../../../utils/dataStateHooks';
import { useRitzTranslation } from '../../../../utils/customHooks';
import { EntitySingleMetricValueViewLayout } from '../EntityLatestSingleMetricValueUIComponent';
import { EntityLatestSingleMetricValueUIComponentCustomSettings } from '..';
import { NO_VALUE } from '../../../../utils/constants';



const useStyle = makeStyles((theme: Theme) => {
    return {

        root: {
            overflow      : 'hidden',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            height        : '100%',
            padding       : theme.spacing(0.5),

            '&.layout-horizontal': {
                flexDirection: 'row',

                '& $valueAndUnit': {
                    flexDirection: 'column',
                    marginLeft   : theme.spacing(1),
                },

                '& $date': {
                    marginLeft: theme.spacing(1),
                },

                '& $action': {
                    marginLeft: theme.spacing(1),
                },
            },

            '&.layout-vertical': {
                flexDirection: 'column',

                '& $label': {
                    textAlign: 'center',
                },

                '& $valueAndUnit': {
                    flexDirection: 'row',
                    marginTop    : theme.spacing(1),
                },

                '& $date': {
                    marginTop: theme.spacing(1),
                    textAlign: 'center',
                },

                '& $action': {
                    marginTop: theme.spacing(1),
                },
            },
        },

        label: {
        },

        valueAndUnit: {
        },

        value: {
            marginLeft: theme.spacing(1),
            whiteSpace: 'nowrap',
        },

        unit: {
            marginLeft: theme.spacing(1),
            whiteSpace: 'nowrap',
        },

        date: {
        },

        action: {
        },
    };
}, { name: 'EntitySingleMetricViewDefault', index: 1 });



/**
 * The props required for EntitySingleMetricViewDefault
 */
export interface EntitySingleMetricViewDefaultProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly customSettings?: EntityLatestSingleMetricValueUIComponentCustomSettings |undefined;
    readonly singleMetricData?: SingleMetricData | undefined;
    readonly action?: {
        readonly icon?: string | undefined;
        readonly label?: string | undefined;
        readonly variant?: ButtonProps['variant'];
        readonly color?: ButtonProps['color'];
        readonly onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>)=> void;
    } | undefined;
}



/**
 * This is a design view for the component that showing 1 entity metric value which shows the info label/value/unit/date horizontally
 */
export const EntitySingleMetricViewDefault: React.FC<EntitySingleMetricViewDefaultProps> = (
    props: EntitySingleMetricViewDefaultProps,
) => {
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();

    const label = useMemo(() => {
        return props.customSettings?.label ? trans(props.customSettings.label) : props.singleMetricData?.name;
    }, [props.customSettings?.label, props.singleMetricData?.name, trans]);

    return (
        <div
            className={clsx(
                props.className, classes.root, `layout-${props.customSettings?.layout ?? EntitySingleMetricValueViewLayout.HORIZONTAL}`)}
            style={{ ...props.style, ...props.customSettings?.containerStyle }}
        >
            {label !== undefined && (
                <Typography
                    className={classes.label}
                    variant="h6"
                    style={props.customSettings?.labelStyle}
                >
                    {label}:
                </Typography>
            )}
            <div className={classes.valueAndUnit}>
                <Typography
                    className={classes.value}
                    variant="h6"
                    style={props.customSettings?.valueStyle}
                >
                    {props.singleMetricData?.value ?? NO_VALUE}
                </Typography>
                {props.customSettings?.showUnit !== false && props.singleMetricData?.unit && (
                    <Typography
                        className={classes.unit}
                        variant="h6"
                        style={props.customSettings?.unitStyle}
                    >
                        {props.singleMetricData?.unit}
                    </Typography>
                )}
            </div>
            {props.customSettings?.showDateTime !== false && props.singleMetricData?.date && (
                <Typography
                    className={classes.date}
                    variant="h6"
                    style={props.customSettings?.dateTimeStyle}
                >
                    {props.customSettings?.layout === EntitySingleMetricValueViewLayout.VERTICAL ? (
                        props.singleMetricData?.date
                    ) : (
                        `(${props.singleMetricData?.date})`
                    )}
                </Typography>
            )}
            {props.action && (
                <>
                    {props.action.icon && !props.action.label ? (
                        <IconButton
                            className={classes.action}
                            size="small"
                            color={props.action.color}
                            onClick={props.action.onClick}
                        >
                            <FontIcon iconName={props.action.icon} />
                        </IconButton>
                    ) : (
                        <Button
                            className={classes.action}
                            startIcon={props.action.icon && <FontIcon iconName={props.action.icon} />}
                            size="small"
                            color={props.action.color}
                            variant={props.action.variant}
                            onClick={props.action.onClick}
                        >
                            {props.action.label && trans(props.action.label)}
                        </Button>
                    )}
                </>
            )}
        </div>
    );
};
